import { useState } from "react";
import { Trash2, FilePlus } from "lucide-react";
import type { BrandDocument } from "@endeks/shared";
import MediaPicker from "../../components/Media/MediaPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function BrandDocumentsField({
  value,
  onChange,
}: {
  value: BrandDocument[];
  onChange: (docs: BrandDocument[]) => void;
}) {
  const [pickingIndex, setPickingIndex] = useState<number | null>(null);

  function updateRow(index: number, patch: Partial<BrandDocument>) {
    onChange(value.map((doc, i) => (i === index ? { ...doc, ...patch } : doc)));
  }

  function removeRow(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function addRow() {
    onChange([...value, { title: "", titleEn: "", url: "" }]);
  }

  function handleSelect(url: string) {
    if (pickingIndex !== null) {
      updateRow(pickingIndex, { url });
    }
    setPickingIndex(null);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Belgeler / Kataloglar (PDF)</h3>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <FilePlus className="h-4 w-4 mr-1" />
          Belge Ekle
        </Button>
      </div>

      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">Henüz belge eklenmedi.</p>
      )}

      <div className="space-y-3">
        {value.map((doc, index) => {
          const filename = doc.url
            ? (doc.url.split("/").pop() ?? doc.url)
            : "Dosya seçilmedi";
          return (
            <div key={index} className="rounded-md border p-4 space-y-3 bg-muted/20">
              <div className="flex items-start gap-3">
                {/* TR title */}
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor={`doc-title-${index}`}>Başlık</Label>
                  <Input
                    id={`doc-title-${index}`}
                    value={doc.title}
                    onChange={(e) => updateRow(index, { title: e.target.value })}
                    placeholder="Katalog adı (TR)"
                  />
                </div>

                {/* EN title */}
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor={`doc-titleEn-${index}`}>Başlık (EN)</Label>
                  <Input
                    id={`doc-titleEn-${index}`}
                    value={doc.titleEn ?? ""}
                    onChange={(e) =>
                      updateRow(index, { titleEn: e.target.value || null })
                    }
                    placeholder="Catalog name (EN)"
                  />
                </div>

                {/* Remove button */}
                <div className="pt-6">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(index)}
                    title="Kaldır"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              {/* PDF picker row */}
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPickingIndex(index)}
                >
                  PDF Seç
                </Button>
                <span className="text-sm text-muted-foreground truncate max-w-xs">
                  {filename}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Single shared MediaPicker — controlled by pickingIndex */}
      <MediaPicker
        defaultFolder="brands"
        open={pickingIndex !== null}
        onClose={() => setPickingIndex(null)}
        onSelect={handleSelect}
      />
    </div>
  );
}
